import { Bar, Box, Cyl, Slab } from '../../render/parts'
import type { Tone } from '../../render/theme'

/**
 * Desk vocabulary: tops, leg systems, pedestals and screens. Every desk in the
 * catalog is a composition of these, which is what keeps proportions consistent
 * across a floor plan.
 */

export const DESK_HEIGHT = 0.74
export const TOP_THICKNESS = 0.028

export interface WorkTopProps {
  width: number
  depth: number
  height?: number
  tone?: Tone
  radius?: number
  thickness?: number
}

export function WorkTop({
  width,
  depth,
  height = DESK_HEIGHT,
  tone = 'surface',
  radius = 0.02,
  thickness = TOP_THICKNESS,
}: WorkTopProps) {
  return <Slab tone={tone} size={[width, depth, thickness]} radius={radius} position={[0, height - thickness / 2, 0]} />
}

export interface PanelLegsProps {
  width: number
  depth: number
  height?: number
  tone?: Tone
  /** Inset of each end panel from the top's edge. */
  inset?: number
  thickness?: number
  /** Render only one side — used by desks that pair a panel with a pedestal. */
  sides?: 'both' | 'left' | 'right'
}

/** Full-height end panels — the quiet, boxy leg system used in the reference. */
export function PanelLegs({
  width,
  depth,
  height = DESK_HEIGHT,
  tone = 'shell',
  inset = 0.04,
  thickness = 0.026,
  sides = 'both',
}: PanelLegsProps) {
  const x = width / 2 - inset - thickness / 2
  const list = sides === 'both' ? [-1, 1] : sides === 'left' ? [-1] : [1]
  return (
    <group>
      {list.map((side) => (
        <Box
          key={side}
          tone={tone}
          size={[thickness, height - TOP_THICKNESS, depth - inset * 2]}
          position={[side * x, (height - TOP_THICKNESS) / 2, 0]}
        />
      ))}
    </group>
  )
}

export interface TubeLegsProps {
  width: number
  depth: number
  height?: number
  tone?: Tone
  r?: number
  inset?: number
}

/** Four round legs with a perimeter rail — lighter tables and meeting desks. */
export function TubeLegs({
  width,
  depth,
  height = DESK_HEIGHT,
  tone = 'metal',
  r = 0.022,
  inset = 0.1,
}: TubeLegsProps) {
  const x = width / 2 - inset
  const z = depth / 2 - inset
  const top = height - TOP_THICKNESS
  return (
    <group>
      {(
        [
          [-x, -z],
          [x, -z],
          [-x, z],
          [x, z],
        ] as const
      ).map(([lx, lz]) => (
        <Cyl key={`${lx},${lz}`} tone={tone} r={r} h={top} position={[lx, top / 2, lz]} segments={16} />
      ))}
      {[-1, 1].map((side) => (
        <Bar key={side} tone={tone} r={r * 0.72} from={[-x, top - 0.09, side * z]} to={[x, top - 0.09, side * z]} />
      ))}
    </group>
  )
}

export interface ModestyPanelProps {
  width: number
  height?: number
  deskHeight?: number
  tone?: Tone
  z?: number
}

/** The back panel that hides legs and cables from the room side. */
export function ModestyPanel({
  width,
  height = 0.34,
  deskHeight = DESK_HEIGHT,
  tone = 'shellSoft',
  z = -0.02,
}: ModestyPanelProps) {
  return (
    <Box
      tone={tone}
      size={[width, height, 0.02]}
      position={[0, deskHeight - TOP_THICKNESS - height / 2 - 0.04, z]}
    />
  )
}

export interface PedestalProps {
  width?: number
  depth?: number
  height?: number
  drawers?: number
  tone?: Tone
  frontTone?: Tone
  handleTone?: Tone
  accent?: string
  /** Adds castors, turning a fixed pedestal into a mobile one. */
  mobile?: boolean
  /** Paints the bottom drawer in the accent colour, as filing units often are. */
  accentDrawer?: boolean
}

/** Drawer pedestal — under desks, or standing alone as a mobile unit. */
export function Pedestal({
  width = 0.42,
  depth = 0.58,
  height = 0.62,
  drawers = 3,
  tone = 'shell',
  frontTone = 'shellSoft',
  handleTone = 'metalDark',
  accent,
  mobile = false,
  accentDrawer = false,
}: PedestalProps) {
  const base = mobile ? 0.05 : 0
  const bodyHeight = height - base
  const gap = 0.008
  const drawerHeight = (bodyHeight - gap * (drawers + 1)) / drawers

  return (
    <group>
      <Box tone={tone} size={[width, bodyHeight, depth]} position={[0, base + bodyHeight / 2, 0]} />
      {Array.from({ length: drawers }, (_, index) => {
        const y = base + gap + drawerHeight / 2 + index * (drawerHeight + gap)
        const isAccent = accentDrawer && index === 0
        return (
          <group key={index}>
            <Slab
              tone={isAccent ? 'accent' : frontTone}
              color={isAccent ? accent : undefined}
              size={[width - 0.018, drawerHeight, 0.014]}
              radius={0.006}
              rotation={[Math.PI / 2, 0, 0]}
              position={[0, y, depth / 2 + 0.007]}
            />
            <Box
              tone={handleTone}
              size={[width * 0.42, 0.012, 0.016]}
              position={[0, y + drawerHeight / 2 - 0.035, depth / 2 + 0.02]}
            />
          </group>
        )
      })}
      {mobile
        ? (
            [
              [-width / 2 + 0.06, -depth / 2 + 0.08],
              [width / 2 - 0.06, -depth / 2 + 0.08],
              [-width / 2 + 0.06, depth / 2 - 0.08],
              [width / 2 - 0.06, depth / 2 - 0.08],
            ] as const
          ).map(([x, z]) => (
            <Cyl key={`${x},${z}`} tone="darkDeep" axis="x" r={0.022} h={0.018} position={[x, 0.024, z]} segments={12} />
          ))
        : null}
    </group>
  )
}

export interface PrivacyPanelProps {
  width: number
  height?: number
  deskHeight?: number
  accent?: string
  tone?: Tone
  /** Thin frame that reads as an aluminium edge around the fabric. */
  framed?: boolean
  z?: number
}

/**
 * The accent-coloured desk screen. This is the single strongest colour note in
 * the reference illustrations, so it is a first-class, reusable part.
 */
export function PrivacyPanel({
  width,
  height = 0.4,
  deskHeight = DESK_HEIGHT,
  accent,
  tone = 'accent',
  framed = true,
  z = 0,
}: PrivacyPanelProps) {
  const y = deskHeight + height / 2 + 0.01
  return (
    <group position={[0, 0, z]}>
      <Slab
        tone={tone}
        color={accent}
        size={[width, height, 0.02]}
        radius={0.008}
        rotation={[Math.PI / 2, 0, 0]}
        position={[0, y, 0]}
      />
      {framed ? (
        <Slab
          tone="metal"
          size={[width + 0.014, 0.018, 0.026]}
          radius={0.008}
          position={[0, y + height / 2 + 0.009, 0]}
        />
      ) : null}
      {[-1, 1].map((side) => (
        <Box
          key={side}
          tone="metalDark"
          size={[0.03, 0.07, 0.05]}
          position={[side * (width / 2 - 0.06), deskHeight - 0.02, 0]}
        />
      ))}
    </group>
  )
}

export interface CableTrayProps {
  width: number
  deskHeight?: number
  tone?: Tone
}

export function CableTray({ width, deskHeight = DESK_HEIGHT, tone = 'metalDark' }: CableTrayProps) {
  return <Box tone={tone} size={[width * 0.6, 0.06, 0.1]} position={[0, deskHeight - 0.14, -0.12]} />
}

/** Cable grommet: a small dark disc set into a worktop. */
export function Grommet({
  x = 0,
  z = 0,
  deskHeight = DESK_HEIGHT,
  r = 0.035,
}: {
  x?: number
  z?: number
  deskHeight?: number
  r?: number
}) {
  return <Cyl tone="darkSoft" r={r} h={0.006} position={[x, deskHeight + 0.002, z]} segments={16} />
}
